import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { FaFilter } from 'react-icons/fa';
import { filters } from '@/constants/filterConfigs';
import { SOSMultiSelectCheckboxDropDown } from '@/components/inputs/multi-select-checkbox-dropdown';
import { SOSCheckBoxes } from '@/components/inputs/check-box';
import { SOSSingleCheckBox } from '@/components/inputs/single-check-box';
import { useFetchQuery } from '@/hooks/useFetchQuery';
import { fetchResourceFiltersApi } from '@/services/resourceServices';
import { addResources } from '@/pages/resources/resourceCardsSlice';
import {
  addResourceFilters,
  clearResourceFilters,
  updateResourcePageNumber,
} from '@/layout/common-filter/filtersSlice';
import { FiltersContainer } from '@/layout/common-filter/styles/Filters.styles';
import { useBaseFilter, convertFinalKey } from './BaseFilter';

/**
 * Resource Filter Component - Filters for resource page
 * @return {JSX.Element} Resource filter component
 */
export const ResourceFilter = () => {
  const page = 'RESOURCE_PAGE';
  const filterData = filters[page];

  // Fetch filter options from API
  const { data } = useFetchQuery({
    currNav: '/resources/filters',
    qryFn: () => fetchResourceFiltersApi(),
    key: 'filterResource',
  });

  // Use base filter hook
  const {
    dispatch,
    resourceFiltersObj,
    reff,
    setReff,
    defaultVal,
    mergedData,
    activeFilterCount,
  } = useBaseFilter({ page, filterData, apiData: data });

  /**
   * Handle filter change
   */
  const handleChange = useCallback(
    (e, filter) => {
      const key = convertFinalKey(filter.name);
      const value = key === 'allResources' ? (e.target.checked ? 1 : 0) : e;

      dispatch(addResources([]));
      dispatch(updateResourcePageNumber(0));
      dispatch(addResourceFilters({ key, value }));
    },
    [dispatch],
  );

  /**
   * Clear all filters
   */
  const clearFilterData = useCallback(() => {
    dispatch(
      clearResourceFilters({
        page: 0,
        resourceNameString: '',
        application: [],
        resourceName: [],
        namespace: [],
        resourceGroup: [],
        allResources: 0,
      }),
    );
    setReff(false);
    setTimeout(() => setReff(true), 1);
  }, [dispatch, setReff]);

  /**
   * Render filter element
   */
  const renderElement = useCallback(
    (filter) => {
      const filterKey = convertFinalKey(filter.name);
      const currentValue = resourceFiltersObj[filterKey];

      switch (filter.type) {
        case 'DROP_DOWN_WITH_FILTER':
          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSMultiSelectCheckboxDropDown
                handleChange={(values) => handleChange(values, filter)}
                placeholder={filter.actualName}
                options={filter.options}
                defaultValue={currentValue || []}
                key={filter.key}
              />
            </div>
          );

        case 'MULTI_SELECT_CHECKBOX_DROPDOWN':
          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSCheckBoxes
                handleChange={(e) => handleChange(e, filter)}
                options={filter.options}
                defaultValue={defaultVal}
                key={filter.key}
              />
            </div>
          );

        case 'SINGLECHECKBOX':
          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSSingleCheckBox
                options={filter.options}
                handleChange={(e) => handleChange(e, filter)}
                defaultValue={defaultVal}
                key={filter.key}
              />
            </div>
          );

        default:
          return null;
      }
    },
    [handleChange, defaultVal, resourceFiltersObj],
  );

  return (
    <FiltersContainer>
      {reff && (
        <>
          <div className="filter-header">
            <h3>
              <span className="header-text">
                <FaFilter className="filter-icon" />
                Resource Filters
              </span>
              {activeFilterCount > 0 && (
                <span className="filter-count">{activeFilterCount}</span>
              )}
            </h3>
          </div>

          <div className="filters-content">
            {mergedData.map((filter) => renderElement(filter))}
          </div>

          <div className="filter-actions">
            <Button
              type="primary"
              danger
              size="large"
              onClick={clearFilterData}
            >
              Clear All Filters
            </Button>
          </div>
        </>
      )}
    </FiltersContainer>
  );
};

ResourceFilter.propTypes = {
  hideFilter: PropTypes.func,
};
